import React, { useEffect, useState } from "react";
import { Card, Grid, TextField, MenuItem, Tab, Tabs, Chip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { skillActions } from "store/skill-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchMemberSkills,
  verifyMemberSkill,
  fetchMemberCertifications,
} from "app/services/skill.service";
import { loadAllMembers } from "app/services/member.service";
import { MemberSkillsDataGrid } from "./MemberSkillsDataGrid";
import { CertificationsDataGrid } from "./CertificationsDataGrid";

export const SkillMatrixManagerView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [memberCerts, setMemberCerts] = useState([]);

  const memberSkills = useSelector((state) => state.skill.memberSkills);
  const user = useSelector((state) => state.user);

  useEffect(() => {
    loadTeamMembers();
  }, []);

  useEffect(() => {
    if (selectedMemberId) {
      loadMemberSkillsData(selectedMemberId);
      loadMemberCertsData(selectedMemberId);
    }
  }, [selectedMemberId]);

  const loadTeamMembers = () => {
    loadAllMembers()
      .then((response) => {
        const memberList = response.data || [];
        setMembers(memberList);
        if (memberList.length > 0) {
          setSelectedMemberId(memberList[0].id);
        }
      })
      .catch((error) => handleAxiosError(error));
  };

  const loadMemberSkillsData = (memberId) => {
    setIsLoading(true);
    fetchMemberSkills(memberId)
      .then((data) => dispatch(skillActions.setMemberSkills(data)))
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  const loadMemberCertsData = (memberId) => {
    fetchMemberCertifications(memberId)
      .then((data) => setMemberCerts(data))
      .catch((error) => handleAxiosError(error));
  };

  const handleVerifySkill = async (memberSkill) => {
    try {
      await verifyMemberSkill(memberSkill.id, user.userId);
      loadMemberSkillsData(selectedMemberId);
    } catch (error) {
      handleAxiosError(error);
    }
  };

  const pendingVerifications = memberSkills.filter((ms) => !ms.isVerified);
  const verifiedSkills = memberSkills.filter((ms) => ms.isVerified);

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="info">Team Members</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{members.length}</MDTypography>
              <MDTypography variant="caption">Total Members</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="success">Verified</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{verifiedSkills.length}</MDTypography>
              <MDTypography variant="caption">Verified Skills</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="warning">Pending</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{pendingVerifications.length}</MDTypography>
              <MDTypography variant="caption">Awaiting Verification</MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Member Selection & Content */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                <MDTypography variant="h6">Select Team Member:</MDTypography>
                <TextField
                  select
                  size="small"
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  sx={{ minWidth: 250 }}
                >
                  {members.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.firstName} {member.lastName}
                    </MenuItem>
                  ))}
                </TextField>
              </MDBox>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label={<MDBox display="flex" alignItems="center" gap={1}>
                  Skills
                  {pendingVerifications.length > 0 && (
                    <Chip size="small" label={pendingVerifications.length} color="warning" />
                  )}
                </MDBox>} />
                <Tab label="Certifications" />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <MDBox>
                <MemberSkillsDataGrid
                  memberSkills={memberSkills}
                  isLoading={isLoading}
                  showActions={false}
                  showVerifyAction={true}
                  onVerify={handleVerifySkill}
                />
              </MDBox>
            )}
            {tabValue === 1 && (
              <MDBox>
                <CertificationsDataGrid
                  certifications={memberCerts}
                  isLoading={isLoading}
                  showActions={false}
                />
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

